import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField } from "@mui/material";
import RadioButtonCheckedRounded from "@mui/icons-material/RadioButtonCheckedRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { getSmartDefaultsForTest } from "./testSmartDefaults";

export const REASSURING = "Reassuring";
const REASSURING_DESCRIPTION = "Nothing concerning. No further detail needed.";
const DETAILED = "detailed";
const MAX_DETAIL_CHARS = 200;

const RadioIcon = ({ selected }) =>
  selected ? (
    <RadioButtonCheckedRounded color="primary" />
  ) : (
    <RadioButtonUncheckedRounded color="disabled" />
  );

/**
 * The body of the result dialog when one answer is chosen from one list.
 *
 * ⚠️ THIS EXISTS BECAUSE THE OLD DIALOG MADE TWO ANSWERS LOOK LIKE TWO QUESTIONS. "Mark as
 * Reassuring" was a button above a divider and the test's own results were a second set of
 * controls below it, so nothing said they were alternatives, and Reassuring saved and closed on
 * the spot while everything else waited for Save. Here they are rows of the same list: picking one
 * replaces the other, and nothing is saved until the dialog's Save.
 *
 * Tests with a structured form (CBC, BMP, imaging…) get two rows instead, "Reassuring" or "Enter
 * detailed results", and the form only appears under the second, for the same reason.
 *
 * Reports what would be saved through onResultChange (null when nothing is chosen). The saved
 * shapes are the ones the rest of ChartMind already reads: a string for a plain choice, or
 * { status, description, prose } when detail was added, or whatever the structured form builds.
 */
const SingleChoiceResult = ({
  test,
  existingResult,
  onResultChange,
  StructuredForm = null,
  notes,
  onNotesChange,
}) => {
  const choices = useMemo(() => {
    const own = StructuredForm ? [] : getSmartDefaultsForTest(test?.name).choices;
    return [REASSURING, ...own.filter((c) => c !== REASSURING)];
  }, [StructuredForm, test?.name]);

  const initialChoice = () => {
    if (!existingResult) return null;
    if (existingResult === REASSURING) return REASSURING;
    if (StructuredForm) return DETAILED;
    return typeof existingResult === "string" ? existingResult : existingResult.status || null;
  };

  const [choice, setChoice] = useState(initialChoice);
  const [detail, setDetail] = useState(
    typeof existingResult === "object" && existingResult?.description
      ? existingResult.description
      : "",
  );
  const [structuredResult, setStructuredResult] = useState(
    StructuredForm && typeof existingResult === "object" ? existingResult : null,
  );

  useEffect(() => {
    const text = detail.trim();
    let result = null;
    if (choice === REASSURING) result = REASSURING;
    else if (choice === DETAILED) result = structuredResult;
    else if (choice && text) {
      result = { status: choice, description: text, prose: `${test?.name || "Test"}: ${choice} - ${text}` };
    } else if (choice) result = choice;
    onResultChange(result);
  }, [choice, detail, structuredResult, test?.name, onResultChange]);

  const rows = StructuredForm
    ? [
        { value: REASSURING, title: REASSURING, description: REASSURING_DESCRIPTION },
        { value: DETAILED, title: "Enter detailed results", description: "Record the values or findings." },
      ]
    : choices.map((c) => ({
        value: c,
        title: c,
        description: c === REASSURING ? REASSURING_DESCRIPTION : undefined,
      }));

  return (
    <Box>
      {/* No visible heading: the dialog's title and line already say what this is. The label is
          for screen readers, which otherwise meet an unnamed group of options. */}
      <Stack spacing={1} role="radiogroup" aria-label={`${test?.name || "Test"} result`}>
        {rows.map((row) => (
          <SelectableChoiceCard
            key={row.value}
            icon={<RadioIcon selected={choice === row.value} />}
            title={row.title}
            description={row.description}
            selected={choice === row.value}
            onClick={() => setChoice(row.value)}
          />
        ))}
      </Stack>

      {choice === DETAILED && StructuredForm && (
        <Box sx={{ mt: 2 }}>
          <StructuredForm
            test={test}
            existingResult={typeof existingResult === "object" ? existingResult : null}
            onResultChange={setStructuredResult}
            notes={notes}
            onNotesChange={onNotesChange}
          />
        </Box>
      )}

      {/* ⚠️ ALWAYS ON SCREEN, ONLY ENABLED WHEN IT APPLIES. Showing it on choosing a result made
          the dialog jump as the clinician clicked. Reassuring takes no detail, so it disables
          the field rather than removing it. */}
      {!StructuredForm && (
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          multiline
          minRows={2}
          label="Detail (optional), e.g. organism or value"
          value={choice === REASSURING ? "" : detail}
          disabled={!choice || choice === REASSURING}
          onChange={(e) => setDetail(e.target.value.slice(0, MAX_DETAIL_CHARS))}
          helperText={`${choice === REASSURING ? 0 : detail.length}/${MAX_DETAIL_CHARS}`}
        />
      )}
    </Box>
  );
};

export default SingleChoiceResult;
